import type { NoteSourceData } from "../../../types/notes.types";

export const DRAFT_PREFIX = "note-draft:v1:";
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h
const LAST_SWEEP_KEY = `${DRAFT_PREFIX}__lastSweep`;

export interface NoteDraft {
	title: string;
	content: unknown; // TipTap JSON
	folderId: string | null;
	timestamp: number; // Date.now() of last write
	baseUpdatedAt: string | null; // server note_updated_at captured at open time
}

export interface DraftKeyArgs {
	isCreateMode: boolean;
	noteId?: string | null;
	notePlace?: string;
	noteSourceData?: NoteSourceData;
	folderId?: string | null;
}

const normalize = (value: string) => value.trim().replace(/\s+/g, " ");

// Stable per-context signature so drafts from different creation origins never collide.
const createSignature = (
	notePlace: string,
	noteSourceData: NoteSourceData | undefined,
	folderId: string | null | undefined,
): string => {
	if (notePlace === "bible") {
		// A single page-wide slot for all Bible-created notes: the anchored passage
		// (and its exact verse selection) is not part of the key, so a user who
		// reselects a slightly different range still recovers their in-progress note.
		return "page";
	}
	if (notePlace === "course" || notePlace === "lesson") {
		const course = normalize(noteSourceData?.course_name ?? "");
		const lesson = normalize(
			String(noteSourceData?.lesson_name ?? noteSourceData?.lesson_num ?? ""),
		);
		return `${course}|${lesson}`;
	}
	return folderId || "root";
};

export function computeDraftKey({
	isCreateMode,
	noteId,
	notePlace = "profile",
	noteSourceData,
	folderId,
}: DraftKeyArgs): string {
	if (!isCreateMode && noteId) {
		return `${DRAFT_PREFIX}id:${noteId}`;
	}
	return `${DRAFT_PREFIX}new:${notePlace}:${createSignature(notePlace, noteSourceData, folderId)}`;
}

export function isDraftStale(entry: unknown, now: number): boolean {
	if (!entry || typeof entry !== "object") return true;
	const timestamp = (entry as { timestamp?: unknown }).timestamp;
	if (typeof timestamp !== "number") return true;
	return now - timestamp > DRAFT_TTL_MS;
}

export function readDraft(key: string): NoteDraft | null {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (isDraftStale(parsed, Date.now())) {
			localStorage.removeItem(key);
			return null;
		}
		return parsed as NoteDraft;
	} catch {
		try {
			localStorage.removeItem(key);
		} catch {
			/* storage unavailable — ignore */
		}
		return null;
	}
}

export function writeDraft(key: string, draft: NoteDraft): void {
	try {
		localStorage.setItem(key, JSON.stringify(draft));
	} catch {
		/* quota exceeded / storage disabled — never break editing */
	}
}

export function removeDraft(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch {
		/* storage unavailable — ignore */
	}
}

export function sweepStaleDrafts(now: number = Date.now()): void {
	try {
		const last = Number(localStorage.getItem(LAST_SWEEP_KEY) ?? 0);
		if (now - last < SWEEP_INTERVAL_MS) return;
		localStorage.setItem(LAST_SWEEP_KEY, String(now));

		// Snapshot keys first — removing during index iteration would skip entries.
		const keys = Object.keys(localStorage).filter(
			k => k.startsWith(DRAFT_PREFIX) && k !== LAST_SWEEP_KEY,
		);
		for (const key of keys) {
			const raw = localStorage.getItem(key);
			let stale = true;
			if (raw) {
				try {
					stale = isDraftStale(JSON.parse(raw), now);
				} catch {
					stale = true;
				}
			}
			if (stale) localStorage.removeItem(key);
		}
	} catch {
		/* storage unavailable — no-op */
	}
}
